import { motion, AnimatePresence } from 'framer-motion';
import { useRouter } from 'next/router';
import cn from 'classnames';
import { ExpandLessIcon } from '@/components/icons/expand-less-icon';
import { ExpandMoreIcon } from '@/components/icons/expand-more-icon';
import { getIcon } from '@/lib/get-icon';
import * as CategoryIcons from '@/components/icons/category';
import { useEffect, useState } from 'react';
import { drawerAtom } from '@/store/drawer-atom';
import { useAtom } from 'jotai';
import { useDrawer } from './drawer/useDrawer';
import { searchDrawerAtom } from '@/store/searchDrawerAtom';
import { Routes } from '@/config/routes';
import { trackCategoryView } from '@/lib/gtm';

interface TreeMenuItemProps {
  item: any;
  className?: string;
  depth?: number;
  selectedCategorySlug?: string;
}
const TreeMenuItem: React.FC<TreeMenuItemProps> = ({
  className,
  item,
  depth = 0,
  selectedCategorySlug,
}) => {
  const router = useRouter();

  const isActive =
    selectedCategorySlug === item.slug ||
    item?.children?.some((_item: any) => _item.slug === selectedCategorySlug);

  const [isOpen, setOpen] = useState<boolean>(isActive);
  const { closeDrawer } = useDrawer({
    atom: searchDrawerAtom,
  });

  useEffect(() => {
    setOpen(isActive);
  }, [isActive]);

  const { name, children: items, icon } = item;
  const [{ display }, setDrawerState] = useAtom(drawerAtom);

  function toggleCollapse() {
    setOpen((prevValue) => !prevValue);
  }

  function onClick() {
    trackCategoryView(name);
    router.push(Routes.category(item.slug));
    closeDrawer();

    if (display) {
      setDrawerState({ display: false, view: '' });
      closeDrawer();
    }
  }

  let expandIcon;
  if (Array.isArray(items) && items.length) {
    expandIcon = !isOpen ? (
      <ExpandLessIcon className="h-3 w-3" />
    ) : (
      <ExpandMoreIcon className="h-3 w-3" />
    );
  }

  return (
    <>
      <motion.li
        initial={false}
        animate={{ backgroundColor: '#ffffff' }}
        className="flex items-center py-1"
      >
        <button type="button"
          onClick={onClick}
          className={cn(
            'flex w-full items-center py-2 font-semibold text-body-dark outline-none transition-all ease-in-expo  focus:text-accent focus:outline-0 focus:ring-0 ltr:text-left rtl:text-right',
            isOpen ? 'text-accent' : 'text-body-dark',
            className ? className : 'text-sm',
            Array.isArray(items) && items.length ? 'flex-1' : 'w-full',
          )}
        >
          {icon && (
            <span className="flex h-5 w-5 items-center justify-center ltr:mr-4 rtl:ml-4">
              {getIcon({
                iconList: CategoryIcons,
                iconName: icon,
                className: 'h-full w-full',
              })}
            </span>
          )}
          <span
            className={cn('mr-2 h-1 w-1 rounded-full', {
            'bg-accent': selectedCategorySlug === item.slug,
            'bg-muted': isActive && selectedCategorySlug !== item.slug,
              'bg-transparent': !isActive,
            })}
          />
          <span className={cn('!font-normal')}>{name}</span>
        </button>
        {Array.isArray(items) && items.length ? (
          <button
            type="button"
            className="ltr:ml-auto ltr:mr-4 rtl:ml-4 rtl:mr-auto p-1 appearance-none border-0 bg-transparent"
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapse();
            }}
            aria-expanded={isOpen}
            aria-label={isOpen ? 'Recolher categoria' : 'Expandir categoria'}
          >
            {expandIcon}
          </button>
        ) : null}
      </motion.li>
      <AnimatePresence initial={false}>
        {Array.isArray(items) && isOpen ? (
          <li>
            <motion.ul
              key="content"
              initial="collapsed"
              animate="open"
              exit="collapsed"
              variants={{
                open: { opacity: 1, height: 'auto' },
                collapsed: { opacity: 0, height: 0 },
              }}
              transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
              className="text-xs ltr:ml-2 rtl:mr-2"
            >
              {items.map((currentItem) => {
                const childDepth = depth + 1;
                return (
                  <TreeMenuItem
                    key={`${item.name}-submenu-${currentItem.name}`}
                    item={currentItem}
                    depth={childDepth}
                    className={cn('text-sm text-body ltr:ml-2 rtl:mr-2')}
                    selectedCategorySlug={selectedCategorySlug}
                  />
                );
              })}
            </motion.ul>
          </li>
        ) : null}
      </AnimatePresence>
    </>
  );
};
interface TreeMenuProps {
  parentKeyId?: string; // dont repeat this value, must be unique
  items: any[];
  className?: string;
  selectedCategorySlug?: string;
}

function TreeMenu({
  parentKeyId = 'default',
  items,
  className,
  selectedCategorySlug,
}: Readonly<TreeMenuProps>) {
  return (
    <ul className={cn('text-xs', className)}>
      {items?.map((item: any) => (
        <TreeMenuItem
          key={`${parentKeyId}-${item.name}`}
          item={item}
          selectedCategorySlug={selectedCategorySlug}
        />
      ))}
    </ul>
  );
}

export default TreeMenu;
